import { useEffect, useState } from "react";
import { crewApi, aiApi } from "@/api";
import { FatigueStatusBadge, ScoreDisplay, getStatusColor } from "@/components/FatigueUtils";
import { Search, Filter, X, Brain, ChevronDown, Clock, Users, RefreshCw } from "lucide-react";

function CrewDetailModal({ member, onClose }) {
  const [replacements, setReplacements] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!member) return;
    crewApi.getReplacements(member.id).then(r => setReplacements(r.data));
  }, [member]);

  const runAI = async () => {
    setAiLoading(true);
    try {
      const r = await aiApi.analyze({ person_type: "crew", person_id: member.id, analysis_type: "fatigue_prediction" });
      setAiAnalysis(r.data.analysis);
    } catch { setAiAnalysis("AI analysis unavailable."); }
    finally { setAiLoading(false); }
  };

  if (!member) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="crew-detail-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="font-bold font-heading text-foreground">{member.name}</h3>
            <div className="text-xs text-muted-foreground mt-0.5">{member.employee_id} · {member.rank} · {member.fleet} · {member.base}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="modal-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Fatigue Profile */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Fatigue Score", member.fatigue_score, "text-lg font-mono"],
              ["Hours (24h)", `${member.hours_last_24h}h`, "text-lg font-mono"],
              ["Hours (7d)", `${member.hours_last_7d}h`, "text-lg font-mono"],
              ["Rest Since Duty", `${member.rest_since_last_duty}h`, "text-lg font-mono"],
            ].map(([lbl, val, cls]) => (
              <div key={lbl} className="bg-background border border-border rounded-sm p-3">
                <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">{lbl}</div>
                <div className={`${cls} ${lbl === "Fatigue Score" ? (member.fatigue_score >= 81 ? "text-red-300" : member.fatigue_score >= 61 ? "text-red-400" : member.fatigue_score >= 31 ? "text-amber-400" : "text-green-400") : "text-foreground"}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <FatigueStatusBadge status={member.status} />
            <span className="text-xs text-muted-foreground">{member.consecutive_duties} consecutive duties · {member.sectors_last_7d} sectors (7d)</span>
          </div>

          {/* AI Analysis */}
          <div className="border border-border rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold flex items-center gap-2">
                <Brain size={12} /> AI Fatigue Analysis
              </div>
              <button onClick={runAI} disabled={aiLoading} className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="run-ai-analysis">
                <RefreshCw size={10} className={aiLoading ? "animate-spin" : ""} />
                {aiLoading ? "Analysing..." : "Run Analysis"}
              </button>
            </div>
            {aiAnalysis ? (
              <p className="text-xs text-muted-foreground leading-relaxed">{aiAnalysis}</p>
            ) : (
              <p className="text-xs text-muted-foreground italic">Click Run Analysis to generate AI fatigue assessment.</p>
            )}
          </div>

          {/* Replacements */}
          {(member.status === "high_risk" || member.status === "do_not_assign") && (
            <div>
              <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-2">Safer Replacements</div>
              <div className="space-y-2">
                {replacements.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No immediate replacements available at this base.</div>
                ) : replacements.map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2 bg-background border border-border rounded-sm">
                    <div className="flex-1">
                      <div className="text-xs font-medium text-foreground">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.rank} · {r.fleet}</div>
                    </div>
                    <FatigueStatusBadge status={r.status} />
                    <ScoreDisplay score={r.fatigue_score} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CrewFatigue() {
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBase, setFilterBase] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    crewApi.getAll().then(r => setCrew(r.data)).finally(() => setLoading(false));
  }, []);

  const bases = [...new Set(crew.map(c => c.base))];
  const filtered = crew.filter(c =>
    (!search || c.name.toLowerCase().includes(search.toLowerCase()) || c.employee_id.toLowerCase().includes(search.toLowerCase())) &&
    (!filterStatus || c.status === filterStatus) &&
    (!filterBase || c.base === filterBase)
  );

  const statusCounts = crew.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="crew-fatigue-board">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 2</div>
          <h1 className="text-2xl font-black font-heading text-foreground">Crew Fatigue Management</h1>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        {[
          ["Fit", statusCounts.fit || 0, "text-green-400"],
          ["Caution", statusCounts.caution || 0, "text-amber-400"],
          ["High Risk", statusCounts.high_risk || 0, "text-red-400"],
          ["Do Not Assign", statusCounts.do_not_assign || 0, "text-red-300"],
        ].map(([lbl, val, col]) => (
          <button key={lbl} onClick={() => setFilterStatus(filterStatus === lbl.toLowerCase().replace(/ /g, "_") ? "" : lbl.toLowerCase().replace(/ /g, "_"))}
            className="bg-card border border-border rounded-sm p-3 text-left hover:border-primary/30 transition-colors" data-testid={`crew-stat-${lbl.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`text-2xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 tracking-wider uppercase">{lbl}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search crew..."
            className="bg-card border border-border rounded-sm pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary w-52"
            data-testid="crew-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="crew-status-filter">
          <option value="">All Status</option>
          <option value="fit">Fit</option>
          <option value="caution">Caution</option>
          <option value="high_risk">High Risk</option>
          <option value="do_not_assign">Do Not Assign</option>
        </select>
        <select value={filterBase} onChange={e => setFilterBase(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="crew-base-filter">
          <option value="">All Bases</option>
          {bases.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {(filterStatus || filterBase || search) && (
          <button onClick={() => { setFilterStatus(""); setFilterBase(""); setSearch(""); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="crew-table">
          <thead>
            <tr className="border-b border-border">
              {["Name / ID", "Type", "Base · Fleet", "Score", "Status", "Hours 24h", "Hours 7d", "Rest", "Consec.", "Action"].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center py-8 text-muted-foreground text-xs">Loading crew data...</td></tr>
            ) : filtered.map(member => (
              <tr key={member.id} className="border-b border-border/50 row-hover cursor-pointer" onClick={() => setSelectedMember(member)} data-testid={`crew-row-${member.employee_id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-foreground text-xs">{member.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{member.employee_id}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{member.rank}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{member.base} · {member.fleet}</td>
                <td className="px-3 py-2.5"><ScoreDisplay score={member.fatigue_score} /></td>
                <td className="px-3 py-2.5"><FatigueStatusBadge status={member.status} /></td>
                <td className="px-3 py-2.5 font-mono text-xs text-foreground">{member.hours_last_24h}h</td>
                <td className="px-3 py-2.5 font-mono text-xs text-foreground">{member.hours_last_7d}h</td>
                <td className="px-3 py-2.5 font-mono text-xs text-foreground">{member.rest_since_last_duty}h</td>
                <td className="px-3 py-2.5 font-mono text-xs text-foreground">{member.consecutive_duties}</td>
                <td className="px-3 py-2.5">
                  <button className="text-xs text-primary hover:underline" data-testid={`view-crew-${member.employee_id}`}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-xs">No crew members match the filter.</div>
        )}
      </div>

      {selectedMember && <CrewDetailModal member={selectedMember} onClose={() => setSelectedMember(null)} />}
    </div>
  );
}
